/**
 * Fetches Swiper only on pages that actually carry a carousel.
 *
 * `layout/theme.liquid` used to load the 151 KB bundle on every page, because a
 * layout runs before any section does and therefore cannot know what the page
 * will contain. This flips the question around: by the time this file runs the
 * page is parsed, so it can simply look.
 *
 * The detection is `.swiper` — Swiper's own container class, which every
 * consumer already has to print for the library to work. That keeps the list of
 * carousels out of here; a section added later is found without editing this
 * file.
 *
 * Consumers are unchanged. They already wait for `window.Swiper` with a retry
 * budget of five seconds, which a fetch started at end-of-parse fits inside
 * comfortably.
 *
 * One documented property does change. `CLAUDE_HANDOFF.md` notes that Swiper was
 * `defer` rather than `async`, so the lightbox falling back to a single photo
 * meant "script blocked" and never "script still loading". An injected script is
 * async, so that race now exists in principle. In practice a shopper has to see
 * a gallery photo before clicking it, and the photo is a larger download than
 * the bundle; the fallback is graceful either way.
 */
(function () {
  const source = document.currentScript && document.currentScript.dataset.swiperSrc;
  let pending = null;

  function load() {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (!source) return Promise.resolve(null);

    if (!pending) {
      pending = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = source;
        // Resolving on error too: every consumer already copes with Swiper never
        // arriving, and a rejected promise nobody handles is just noise.
        script.onload = () => resolve(window.Swiper || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      });
    }

    return pending;
  }

  function loadIfNeeded() {
    if (document.querySelector('.swiper')) load();
  }

  // Exposed for anything that builds a carousel after load and cannot rely on
  // the markup having been there to find.
  window.themeSwiper = { load: load };

  loadIfNeeded();

  // The theme editor drops a section into a page that may have had no carousel
  // at all, and its markup arrives long after this ran.
  document.addEventListener('shopify:section:load', loadIfNeeded);
})();
